import React, { memo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import Icons from "../../../assets/Icons";
import { COPILOT_RECIPIENT, COPILOT_RECOMMENDATION } from "../types/CommonTypes";
import RecipientPicker from "./RecipientPicker";

function formatReason(raw: string): string {
  return raw
    .split("_")
    .map((w) => w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const RECOMMEND_ACCENT = "#5921F2";
const RECOMMEND_BG = "#FAF5FF";
const RECOMMEND_BORDER = "#D8C4FF";

interface RecommendCardProps {
  recommendation: COPILOT_RECOMMENDATION;
  onApprove?: (editedMessage?: string) => void;
  onDismiss?: () => void;
}

const RecommendCard = memo(function RecommendCard({
  recommendation,
  onApprove,
  onDismiss,
}: RecommendCardProps) {
  const { t } = useTranslation();
  const { status, successMessage, initialRecipients, recipientOptions, initialMessage } = recommendation;

  // Form state — local, initialised once from recommendation data.
  const [selectedRecipients, setSelectedRecipients] = useState<COPILOT_RECIPIENT[]>(
    initialRecipients ?? []
  );
  const { title } = recommendation;
  const [messageText, setMessageText] = useState(initialMessage ?? title ?? "");
  const [isEditing, setIsEditing] = useState(false);

  const toggleRecipient = (opt: COPILOT_RECIPIENT) => {
    setSelectedRecipients((prev) =>
      prev.some((r) => r.id === opt.id) ? prev.filter((r) => r.id !== opt.id) : [...prev, opt]
    );
  };

  const removeRecipient = (id: string) => {
    setSelectedRecipients((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <Box
      sx={{
        mt: 1,
        ml: 3,
        borderRadius: "12px",
        bgcolor: RECOMMEND_BG,
        border: `1px solid ${RECOMMEND_BORDER}`,
        p: 1.75,
      }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" gap={0.75} mb={1}>
        <Icons.Warning width={16} height={16} style={{ flexShrink: 0, color: RECOMMEND_ACCENT }} />
        <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 700, color: RECOMMEND_ACCENT, lineHeight: "20px" }}>
          {t("Copilot_Recommend_Heading")}
        </Typography>
        {!status && (
          <Box
            component="button"
            onClick={() => setIsEditing((p) => !p)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.375,
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              color: RECOMMEND_ACCENT,
              fontSize: 13,
              fontWeight: 600,
              p: 0,
              "&:hover": { opacity: 0.8 },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
            {t("Copilot_Recommend_Edit")}
          </Box>
        )}
      </Box>

      {/* Title + Reason — shown in all states */}
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "text.primary", lineHeight: "24px", mb: 0.5 }}>
        {recommendation.title}
      </Typography>
      {recommendation.reason && (
        <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: "20px", mb: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
            {t("Copilot_Recommend_Reason_Label")}{" "}
          </Box>
          {formatReason(recommendation.reason)}
        </Typography>
      )}

      {/* ── Idle ── */}
      {!status && (
        <>
          {/* Expanded edit form — shown only when user clicks Edit */}
          {isEditing && (
            <>
              <RecipientPicker
                selectedRecipients={selectedRecipients}
                recipientOptions={recipientOptions}
                onToggle={toggleRecipient}
                onRemove={removeRecipient}
              />

              {/* MESSAGE label */}
              <Typography
                sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "text.primary", mb: 0.75 }}
              >
                {t("Copilot_Recommend_Message_Label")}
              </Typography>

              {/* Message textarea */}
              <Box
                component="textarea"
                value={messageText}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessageText(e.target.value)}
                rows={3}
                sx={{
                  display: "block",
                  width: "100%",
                  resize: "none",
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  px: 1.25,
                  py: 1,
                  fontSize: 13,
                  lineHeight: "20px",
                  fontFamily: "inherit",
                  color: "inherit",
                  bgcolor: "#FFFFFF",
                  mb: 1.5,
                  boxSizing: "border-box",
                  "&:focus": { outline: "none", border: `1px solid ${RECOMMEND_ACCENT}` },
                }}
              />
            </>
          )}

          {/* Send + Dismiss — always visible in idle state */}
          <Box display="flex" gap={1.25} mt={isEditing ? 0 : 0.5}>
            <Box
              component="button"
              onClick={() => onApprove?.(isEditing ? messageText : undefined)}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: RECOMMEND_ACCENT,
                color: "#FFFFFF",
                border: "none",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Recommend_Send")}
            </Box>
            <Box
              component="button"
              onClick={onDismiss}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: "#FFFFFF",
                color: "text.primary",
                border: "1px solid #D8C4FF",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Recommend_Dismiss")}
            </Box>
          </Box>
        </>
      )}

      {/* ── Executing ── */}
      {status === "executing" && (
        <Box sx={{ borderRadius: "10px", bgcolor: "#E3EFFF", px: 1.5, py: 1.25 }}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <CircularProgress size={16} sx={{ color: "#155DFC", flexShrink: 0 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#193CB8", lineHeight: "20px" }}>
              {t("Copilot_Recommend_Executing")}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "#1447E6", lineHeight: "19px" }}>
            {t("Copilot_Recommend_Executing_Sub")}
          </Typography>
        </Box>
      )}

      {/* ── Success ── */}
      {status === "success" && (
        <Box
          sx={{
            borderRadius: "10px",
            bgcolor: "#DCFCE7",
            px: 1.5,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Icons.AlertSuccess width={18} height={18} style={{ flexShrink: 0 }} />
          <Typography sx={{ fontSize: 14, lineHeight: "20px", color: "#007A02" }}>
            {successMessage ? (
              <>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {t("Copilot_Recommend_Success_Prefix")}
                </Box>
                {` ${successMessage}`}
              </>
            ) : (
              t("Copilot_Recommend_Success")
            )}
          </Typography>
        </Box>
      )}

      {/* ── Dismissed ── */}
      {status === "dismissed" && (
        <Box
          sx={{
            borderRadius: "10px",
            bgcolor: "#FFE9FF",
            px: 1.5,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Icons.Noted width={18} height={18} style={{ flexShrink: 0, color: "#E00000" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#E00000", lineHeight: "20px" }}>
            {t("Copilot_Recommend_Dismissed")}
          </Typography>
        </Box>
      )}

      {/* ── Error — Retry + Dismiss ── */}
      {status === "error" && (
        <>
          <Box
            sx={{
              borderRadius: "12px",
              bgcolor: "#F8E5E9",
              border: "1px solid #FFC9C9",
              px: 1.75,
              py: 1.5,
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#E00000", lineHeight: "24px", mb: 0.5 }}>
              {t("Copilot_Recommend_Error_Heading")}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#E00000", lineHeight: "20px" }}>
              {t("Copilot_Recommend_Error_Body")}
            </Typography>
          </Box>
          <Box display="flex" gap={1.25}>
            <Box
              component="button"
              onClick={() => onApprove?.(isEditing ? messageText : undefined)}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: RECOMMEND_ACCENT,
                color: "#FFFFFF",
                border: "none",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Recommend_Retry")}
            </Box>
            <Box
              component="button"
              onClick={onDismiss}
              sx={{
                flex: 1,
                py: 1.25,
                borderRadius: "12px",
                bgcolor: "#FFFFFF",
                color: "text.primary",
                border: "1px solid #D8C4FF",
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { opacity: 0.9 },
                "&:active": { opacity: 0.8 },
              }}
            >
              {t("Copilot_Recommend_Dismiss")}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
});

export default RecommendCard;
